import { Hand, MicOff, MoreHorizontal, Pause, WifiOff } from 'lucide-react';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { Participant } from '../../shared/protocol';
import { Avatar, VideoView } from '../components/ui';
import type { Quality } from '../meeting/Peer';
import { ParticipantMenu } from './ParticipantMenu';
import type { MeetingSession } from '../meeting/MeetingSession';

export interface TileProps {
  session: MeetingSession;
  participant: Participant;
  stream: MediaStream | null;
  showVideo: boolean;
  isSelf: boolean;
  speaking: boolean;
  quality: Quality | undefined;
  peerState: RTCPeerConnectionState | undefined;
  mirror: boolean;
  showName: boolean;
  reactions: { key: number; emoji: string }[];
  amHost: boolean;
  style?: CSSProperties;
  screen?: boolean;
}

/** One participant (or one shared screen) on the stage. */
export function VideoTile(p: TileProps) {
  const [hasFrames, setHasFrames] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLButtonElement>(null);
  const holder = useRef<HTMLDivElement>(null);
  const videoTrack = p.stream?.getVideoTracks()[0];

  // Hide the black frame between "video on" and the first decoded frame.
  useEffect(() => {
    setHasFrames(false);
    const video = holder.current?.querySelector('video');
    if (!video) return;
    const ready = () => setHasFrames(video.videoWidth > 0);
    video.addEventListener('loadeddata', ready);
    video.addEventListener('resize', ready);
    ready();
    return () => {
      video.removeEventListener('loadeddata', ready);
      video.removeEventListener('resize', ready);
    };
  }, [p.stream, videoTrack, p.showVideo]);

  const connecting = !p.isSelf && p.peerState && p.peerState !== 'connected' && p.participant.connected;
  const lost = !p.isSelf && !p.participant.connected;
  const label = p.screen ? `Pantalla de ${p.participant.name}` : `${p.participant.name}${p.isSelf ? ' (Yo)' : ''}`;
  const canMenu = !p.screen && (p.amHost || p.isSelf);

  return (
    <div
      ref={holder}
      className={`tile ${p.speaking && !p.screen ? 'speaking' : ''} ${p.screen ? 'screen' : ''}`}
      style={p.style}
      role="group"
      aria-label={`${label}${p.participant.audioMuted && !p.screen ? ', micrófono silenciado' : ''}`}
      data-participant={p.participant.id}
      data-video={p.showVideo && hasFrames ? 'on' : 'off'}
    >
      {p.showVideo && p.stream ? (
        <VideoView stream={p.stream} mirror={p.mirror} fit={p.screen ? 'contain' : 'cover'} />
      ) : null}
      {(!p.showVideo || !p.stream || !hasFrames) && !p.screen && (
        <div className="tile-avatar">
          <Avatar name={p.participant.name} seed={p.participant.id} size={72} />
        </div>
      )}

      {lost ? (
        <div className="tile-overlay">
          <WifiOff size={22} />
          Reconectando…
        </div>
      ) : p.screen && p.participant.sharePaused ? (
        <div className="tile-overlay paused">
          <Pause size={22} />
          {p.participant.name} ha pausado la pantalla compartida
        </div>
      ) : connecting && p.peerState !== 'new' ? (
        <div className="tile-overlay">
          <div className="spinner" />
          {p.peerState === 'failed' ? 'Sin conexión de medios' : 'Conectando…'}
        </div>
      ) : null}

      <div className="tile-badges">
        {p.participant.handRaised && !p.screen && (
          <span className="tile-badge" title="Mano levantada">
            <Hand size={14} />
          </span>
        )}
        {p.quality === 'poor' && !p.isSelf && (
          <span className="tile-badge warn" title="Conexión inestable">
            <WifiOff size={14} />
          </span>
        )}
      </div>

      {(p.showName || p.screen) && (
        <div className="tile-name">
          {p.participant.audioMuted && !p.screen && <MicOff size={13} className="mic-off" aria-hidden />}
          <span>{label}</span>
        </div>
      )}

      {canMenu && (
        <button
          ref={menuRef}
          type="button"
          className="icon-btn tile-menu-btn"
          aria-label={`Opciones de ${p.participant.name}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <MoreHorizontal size={16} />
        </button>
      )}
      {menuOpen && <ParticipantMenu session={p.session} participant={p.participant} isSelf={p.isSelf} anchor={menuRef} onClose={() => setMenuOpen(false)} placement="bottom" />}

      {p.reactions.map((r) => (
        <span key={r.key} className="tile-reaction" aria-hidden>
          {r.emoji}
        </span>
      ))}
    </div>
  );
}
